import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from './store';
import {
  PaginatedData,
  getEmailBehaviourReport,
  getPhishFitScoreReport
} from '../api/platform';
import { GridSortDirection } from '@mui/x-data-grid';

export interface EmailBehaviourRow {
  first_name: string;
  last_name: string;
  email: string;
  learner_id: number;
  email_sent: number;
  emails_clicked: number;
  emails_reported: number;
  click_through_rate: number;
}

export interface PhishFitScoreRow {
  first_name: string;
  last_name: string;
  email: string;
  learner_id: number;
  click_through_rate: number;
  course_completion: number;
  phish_fit_score: number | null;
}

export interface ManagerReportParams {
  per_pages: number;
  page: number;
  keyword: string;
  sort_column: string;
  sort_type: GridSortDirection;
}

export interface ManagerTeamState {
  isEmailBehaviourLoading: boolean;
  isPhishFitScoreLoading: boolean;
  error: string | undefined;
  emailBehaviourData: PaginatedData<EmailBehaviourRow>;
  phishFitScoreData: PaginatedData<PhishFitScoreRow>;
}

const initialState: ManagerTeamState = {
  isEmailBehaviourLoading: false,
  isPhishFitScoreLoading: false,
  error: undefined,
  emailBehaviourData: {
    total_items: 0,
    total_page: 0,
    start_item: 0,
    end_item: 0,
    list: []
  },
  phishFitScoreData: {
    total_items: 0,
    total_page: 0,
    start_item: 0,
    end_item: 0,
    list: []
  }
};

export const getEmailBehaviourReportEvent = createAsyncThunk(
  'managerTeam/getEmailBehaviourReport',
  async (params: ManagerReportParams) => {
    const response: any = await getEmailBehaviourReport(params);
    return (
      response ?? {
        end_item: 0,
        start_item: 0,
        total_items: 0,
        total_page: 0,
        list: []
      }
    );
  }
);

export const getPhishFitScoreReportEvent = createAsyncThunk(
  'managerTeam/getPhishFitScoreReport',
  async (params: ManagerReportParams) => {
    const response: any = await getPhishFitScoreReport(params);
    return (
      response ?? {
        end_item: 0,
        start_item: 0,
        total_items: 0,
        total_page: 0,
        list: []
      }
    );
  }
);

export const managerTeamSlice = createSlice({
  name: 'managerTeam',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Email Behaviour Report
    builder.addCase(getEmailBehaviourReportEvent.pending, (state) => {
      state.isEmailBehaviourLoading = true;
    });
    builder.addCase(getEmailBehaviourReportEvent.fulfilled, (state, action) => {
      state.isEmailBehaviourLoading = false;
      state.emailBehaviourData = action.payload;
      state.error = undefined;
    });
    builder.addCase(getEmailBehaviourReportEvent.rejected, (state, action) => {
      state.isEmailBehaviourLoading = false;
      state.error = action.payload as string;
    });

    // Phish Fit Score
    builder.addCase(getPhishFitScoreReportEvent.pending, (state) => {
      state.isPhishFitScoreLoading = true;
    });
    builder.addCase(getPhishFitScoreReportEvent.fulfilled, (state, action) => {
      state.isPhishFitScoreLoading = false;
      state.phishFitScoreData = action.payload;
      state.error = undefined;
    });
    builder.addCase(getPhishFitScoreReportEvent.rejected, (state, action) => {
      state.isPhishFitScoreLoading = false;
      state.error = action.payload as string;
    });
  }
});

export const managerTeamDataSelector = (state: RootState) => state.managerTeamEvents;

export default managerTeamSlice.reducer;
